import { ACCESS_TOKEN, REFRESH_TOKEN } from '../constants/storage';
import getStorageWithExpiry from './getStorageWithExpiry';

export interface SuccessData {
    token_type: string;
    expires_in: number;
    access_token: string;
    refresh_token: string;
}

const EXPIRY_MARGIN_MS = 60000;
const REFRESH_TIMEOUT_MS = 10000;

const getTokenExpiry = (token: string): number | null => {
    try {
        const [, payload] = token.split('.');
        if (!payload) {
            return null;
        }
        const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
        return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
        return null;
    }
};

const isAccessTokenExpiring = (): boolean => {
    const token = getStorageWithExpiry('local')?.getItem(ACCESS_TOKEN);
    if (!token) {
        return false;
    }

    const expiry = getTokenExpiry(token);
    if (expiry === null) {
        return false;
    }

    return Date.now() >= expiry - EXPIRY_MARGIN_MS;
};

const login = async (authCode: string): Promise<boolean> => {
    const formData = new FormData();
    formData.append('grant_type', 'authorization_code');
    formData.append('client_id', 'frontend');
    formData.append('scope', 'internal');
    formData.append('code', authCode);
    const storageUtils = getStorageWithExpiry('local');

    try {
        const result = await fetch(`${process.env.NEXT_PUBLIC_CORE_API_URL}/api/public/oauth/token/`, {
            method: 'POST',
            // https://github.com/microsoft/TypeScript/issues/30584
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            body: formData as any,
        });

        if (result.ok) {
            const data: SuccessData = await result.json();
            storageUtils?.setItem(ACCESS_TOKEN, data.access_token);
            storageUtils?.setItem(REFRESH_TOKEN, data.refresh_token);
            return true;
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
    }
    return false;
};

const logout = () => {
    const storageUtils = getStorageWithExpiry('local');
    storageUtils?.removeItem(ACCESS_TOKEN);
    storageUtils?.removeItem(REFRESH_TOKEN);
};

const performRefresh = async (): Promise<boolean> => {
    const storageUtils = getStorageWithExpiry('local');
    const token = storageUtils?.getItem(REFRESH_TOKEN);
    if (!token) {
        return false;
    }

    const formData = new FormData();
    formData.append('grant_type', REFRESH_TOKEN);
    formData.append('client_id', 'frontend');
    formData.append(REFRESH_TOKEN, token);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);

    try {
        const result = await fetch(`${process.env.NEXT_PUBLIC_CORE_API_URL}/api/public/oauth/token/`, {
            method: 'POST',
            // https://github.com/microsoft/TypeScript/issues/30584
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            body: formData as any,
            signal: controller.signal,
        });

        const tokenChanged = storageUtils?.getItem(REFRESH_TOKEN) !== token;

        if (result.status === 400 || result.status === 401) {
            if (!tokenChanged) {
                logout();
            }
            return false;
        }

        if (!result.ok || tokenChanged) {
            return false;
        }

        const data: SuccessData = await result.json();

        if (!data?.access_token || !data?.refresh_token) {
            return false;
        }

        storageUtils?.setItem(ACCESS_TOKEN, data.access_token);
        storageUtils?.setItem(REFRESH_TOKEN, data.refresh_token);
        return true;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return false;
    } finally {
        clearTimeout(timeout);
    }
};

let refreshing: Promise<boolean> | null = null;

const refresh = (): Promise<boolean> => {
    if (!refreshing) {
        refreshing = performRefresh().finally(() => {
            refreshing = null;
        });
    }

    return refreshing;
};

export { login, refresh, logout, isAccessTokenExpiring, ACCESS_TOKEN, REFRESH_TOKEN };
